'use client';

import { Suspense, useEffect, useRef, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ActionIcon, Badge, Box, Button, Group, Menu, Modal, Paper, ScrollArea, Text, Transition } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { IconHistory, IconMaximize, IconMinimize, IconPlus, IconSparkles, IconX } from '@tabler/icons-react';

import { ChatInput } from '@components/chatbot/ChatInput';
import { ChatMessages } from '@components/chatbot/ChatMessages';
import { TruncatedTooltip } from '@components/chatbot/TruncatedTooltip';

import { useChatbot } from '@hooks/chatbot/useChatbot';
import { useFileProcessor } from '@hooks/chatbot/useFileProcessor';

import { useAppContext } from '@context/AppContext';


export function ChatbotWidget() {
    return (
        <Suspense fallback={null}>
            <ChatbotWidgetContent />
        </Suspense>
    );
}

function ChatbotWidgetContent() {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [user] = useAppContext('user');

    const tabParam = searchParams.get('tab');
    const inChatbotTab = pathname === '/' && (!tabParam || tabParam === 'chatbot');
    if (pathname?.startsWith('/login') || !user || inChatbotTab) {
        return null;
    }

    return <ChatbotWidgetInner />;
}

function ChatbotWidgetInner() {
    const t = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); 
    const [tabToDelete, setTabToDelete] = useState<string | null>(null);
    const [openedClearModal, { open: openClearModal, close: closeClearModal }] = useDisclosure(false);
    const [showBubble, setShowBubble] = useState(false);
    const [isHoveredWidget, setIsHoveredWidget] = useState(false);
    const [isFirstVisit, setIsFirstVisit] = useState(true);
    const [isDismissedThisSession, setIsDismissedThisSession] = useState(false);

    // Raccourci clavier pour ouvrir/fermer le widget
    useHotkeys([
        ['mod+shift+K', () => setIsOpen((prev) => !prev)],
    ]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const dismissed = localStorage.getItem('chatbot_tooltip_dismissed') === 'true';
            if (dismissed) {
                setIsFirstVisit(false);
            }
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setShowBubble(false);
            if (isFirstVisit) {
                setIsFirstVisit(false);
                if (typeof window !== 'undefined') localStorage.setItem('chatbot_tooltip_dismissed', 'true');
            }
            return;
        }

        if (isDismissedThisSession) {
            return;
        }

        let hideTimer: NodeJS.Timeout;

        const bubbleTimer = setTimeout(() => {
            setShowBubble(true);

            if (!isFirstVisit) {
                hideTimer = setTimeout(() => {
                    setShowBubble(false);
                }, 5000);
            }
        }, 3000);

        return () => {
            clearTimeout(bubbleTimer);
            if (hideTimer) clearTimeout(hideTimer);
        };
    }, [isOpen, isFirstVisit, isDismissedThisSession]);

    const {
        activeId,
        messages,
        input,
        setInput,
        attachments,
        setAttachments,
        createConversation,
        switchConversation,
        deleteConversation,
        isLoading,
        isExecutingTool,
        executingToolLabel,
        handleSubmit,
        streamingText,
        sendMessage,
        stop,
        submitEditedMessage,
        quota,
        isApiAvailable,
        groupedConversations,
        generatingTitleId,
    } = useChatbot();

    const { isDragging, dragEvents } = useFileProcessor(attachments, setAttachments);

    const inputRef = useRef<HTMLInputElement>(null);
    
    useEffect(() => {
        if (isOpen && !isLoading) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen, isLoading]);
    
    // Confirm delete conversation
    const handleConfirmClear = () => {
        if (tabToDelete) {
            deleteConversation(tabToDelete);
            setTabToDelete(null);
        }
        closeClearModal();
    };

    const handleActionClick = (key: string, actionLabel: string) => {
        const technicalPrompt = t('chatbot-action-technical-prompt', { key });
        const formattedMessage = `[ACTION:${actionLabel}]${technicalPrompt}`;
        sendMessage(formattedMessage);
    };

    if (!isApiAvailable) {
        return null;
    }

    const widgetStyles = isExpanded 
        ? {
            bottom: 20, 
            right: 20,
            width: 'min(900px, calc(100vw - 40px))',
            height: 'calc(100svh - 40px)',
        } : {
            bottom: 70, 
            right: 20,
            width: 'min(400px, calc(100vw - 40px))',
            height: 'min(600px, calc(100svh - 80px))',
        };

    return (
        <>
            {/* Modal de confirmation */}
            <Modal 
                opened={openedClearModal} 
                onClose={closeClearModal} 
                title={t('clear-conversation')} 
                centered 
                zIndex={2000}
                radius="md"
            >
                <Text size="sm" mb="xl">
                    {t('clear-conversation-warning')}
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => { setTabToDelete(null); closeClearModal(); }}>
                        {t('cancel')}
                    </Button>
                    <Button color="red" onClick={handleConfirmClear}>
                        {t('clear')}
                    </Button>
                </Group>
            </Modal>

            <Transition mounted={!isOpen} transition="pop-bottom-right" duration={300} timingFunction="cubic-bezier(0.25, 0.8, 0.25, 1)">
                {(styles) => (
                    <Box 
                        style={{ ...styles, position: 'fixed', bottom: 70, right: 20, zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 16 }}
                        onMouseEnter={() => setIsHoveredWidget(true)}
                        onMouseLeave={() => setIsHoveredWidget(false)}
                    >
                        <Transition mounted={showBubble || isHoveredWidget} transition="pop-bottom-right" duration={400} timingFunction="ease">
                            {(bubbleStyles) => (
                                <Paper 
                                    shadow="xl" 
                                    p="md" 
                                    withBorder 
                                    style={{ 
                                        ...bubbleStyles, 
                                        backgroundColor: 'white', 
                                        position: 'relative', 
                                        marginBottom: 4,
                                        borderRadius: '16px 16px 4px 16px',
                                        border: '1px solid var(--mantine-color-violet-2)',
                                    }}
                                >
                                    <ActionIcon 
                                        size="sm" 
                                        variant="subtle" 
                                        color="gray" 
                                        style={{ position: 'absolute', top: 4, right: 4 }}
                                        onClick={(e) => { e.stopPropagation(); setIsDismissedThisSession(true); setIsFirstVisit(false); setShowBubble(false); setIsHoveredWidget(false); if (typeof window !== 'undefined') localStorage.setItem('chatbot_tooltip_dismissed', 'true'); }}
                                    >
                                        <IconX size={14} />
                                    </ActionIcon>
                                    <Text size="md" fw={700} variant="gradient" gradient={{ from: 'violet', to: 'pink', deg: 45 }} pr={16}>
                                        {t('chatbot-tooltip-title')}
                                    </Text>
                                    <Text size="md" c="dimmed" mt={4} style={{ maxWidth: 220 }}>
                                        {t('chatbot-tooltip-desc')}
                                    </Text>
                                    <Box 
                                        style={{
                                            position: 'absolute',
                                            bottom: -6,
                                            right: 24,
                                            width: 12,
                                            height: 12,
                                            backgroundColor: 'white',
                                            borderRight: '1px solid var(--mantine-color-violet-2)',
                                            borderBottom: '1px solid var(--mantine-color-violet-2)',
                                            transform: 'rotate(45deg)',
                                        }}
                                    />
                                </Paper>
                            )}
                        </Transition>

                        <Button
                            className="btn-gradient-hover"
                            size="xl"
                            variant="gradient"
                            onClick={() => setIsOpen(true)}
                            aria-label={t('open-chatbot')}
                            style={{
                                boxShadow: '0 8px 32px rgba(132, 94, 247, 0.35)',
                                transition: 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                width: 60, 
                                height: 60,
                                padding: 0,
                                borderRadius: '28px 28px 6px 28px',
                                backgroundImage: 'radial-gradient(circle at 90% 90%, var(--mantine-color-nli-liberty-7) 20%, var(--mantine-color-nli-pink-lavender-6) 70%)', 
                            }}
                        >
                            <IconSparkles size={28} style={{ flexShrink: 0 }} />
                        </Button>
                    </Box>
                )}
            </Transition>

            <Transition mounted={isOpen} transition="pop-bottom-right" duration={300} timingFunction="cubic-bezier(0.25, 0.8, 0.25, 1)">
                {(styles) => (
                    <Paper
                        shadow="xl"
                        radius="lg"
                        withBorder
                        {...dragEvents}
                        style={{
                            position: 'fixed', 
                            zIndex: 1000,
                            display: 'flex', 
                            flexDirection: 'column',
                            overflow: 'hidden',
                            ...widgetStyles,
                            ...styles,
                            transitionProperty: 'opacity, transform, width, height, bottom, right',
                            transitionDuration: '0.3s',
                            transitionTimingFunction: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
                        }}
                    >
                        <Box bg="nli-yankees-blue">
                            <Group justify="space-between" p="md" c="white">
                                <Group justify="flex-start" gap="sm">
                                    <Text fw={500}>
                                        {t('chatbot-header-title')}
                                    </Text>
                                    <Badge variant="outline" color="nli-liberty.4">BETA</Badge>
                                </Group>
                        
                                {/* Groupe d'icônes en haut à droite */}
                                <Group gap="xs">
                                    <Menu position="bottom-end" width={260} shadow="md" zIndex={2000}>
                                        <Menu.Target>
                                            <ActionIcon variant="subtle" c="white" aria-label="Historique" title="Historique">
                                                <IconHistory size={20} />
                                            </ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <style>
                                                {`
                                                @keyframes sweep {
                                                    0% { transform: translateX(-100%); }
                                                    100% { transform: translateX(100%); }
                                                }
                                            `}
                                            </style>
                                            <Menu.Item 
                                                leftSection={<IconPlus size={14} />}
                                                onClick={createConversation}
                                            >
                                                {t('new-conversation')}
                                            </Menu.Item>
                                            
                                            {groupedConversations.length > 0 && (
                                                <>
                                                    <Menu.Divider />
                                                    <ScrollArea.Autosize mah={300} type="hover">
                                                        {groupedConversations.map((group) => (
                                                            <Box key={group.label} mb="xs">
                                                                <Menu.Label>
                                                                    {group.label}
                                                                </Menu.Label>
                                                                {group.items.map((conv) => (
                                                                    <Menu.Item 
                                                                        key={conv.id}
                                                                        onClick={() => switchConversation(conv.id)}
                                                                        style={{
                                                                            backgroundColor: activeId === conv.id ? 'var(--mantine-color-nli-liberty-0)' : undefined,
                                                                            color: activeId === conv.id ? 'var(--mantine-color-nli-liberty-7)' : undefined,
                                                                            fontWeight: activeId === conv.id ? 600 : 400,
                                                                            position: 'relative',
                                                                            overflow: 'hidden',
                                                                        }}
                                                                    >
                                                                        {generatingTitleId === conv.id && (
                                                                            <Box
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    top: 0, bottom: 0, left: 0, right: 0,
                                                                                    background: 'linear-gradient(90deg, transparent, rgba(132, 94, 247, 0.15), transparent)',
                                                                                    animation: 'sweep 1.5s infinite linear',
                                                                                    pointerEvents: 'none',
                                                                                    zIndex: 1,
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <Group justify="space-between" wrap="nowrap">
                                                                            <TruncatedTooltip 
                                                                                label={conv.title} 
                                                                                openDelay={500} 
                                                                                position="left" 
                                                                                withArrow
                                                                                textProps={{ size: 'sm' }}
                                                                                style={{ maxWidth: 190, display: 'block' }}
                                                                            >
                                                                                {conv.title}
                                                                            </TruncatedTooltip>
                                                                            <ActionIcon 
                                                                                component="div"
                                                                                size="xs" 
                                                                                variant="subtle"
                                                                                color="gray"
                                                                                onClick={(e) => { 
                                                                                    e.stopPropagation(); 
                                                                                    setTabToDelete(conv.id); 
                                                                                    openClearModal(); 
                                                                                }}
                                                                            >
                                                                                <IconX size={12}/>
                                                                            </ActionIcon>
                                                                        </Group>
                                                                    </Menu.Item>
                                                                ))}
                                                            </Box>
                                                        ))}
                                                    </ScrollArea.Autosize>
                                                </>
                                            )}
                                        </Menu.Dropdown>
                                    </Menu>

                                    <ActionIcon 
                                        variant="subtle" 
                                        c="white" 
                                        onClick={() => setIsExpanded(!isExpanded)} 
                                        aria-label={isExpanded ? t('minimize') : t('maximize')}
                                        title={isExpanded ? t('minimize') : t('maximize')}
                                    >
                                        {isExpanded ? <IconMinimize size={20} /> : <IconMaximize size={20} />}
                                    </ActionIcon>

                                    <ActionIcon 
                                        variant="subtle" 
                                        c="white" 
                                        onClick={() => setIsOpen(false)} 
                                        aria-label={t('close-chatbot')} 
                                    >
                                        <IconX size={20} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        </Box>

                        <ChatMessages
                            messages={messages}
                            isLoading={isLoading}
                            isExecutingTool={isExecutingTool}
                            executingToolLabel={executingToolLabel}
                            streamingText={streamingText}
                            onEditLastMessage={submitEditedMessage}
                            onActionClick={handleActionClick}
                            size="sm"
                        />

                        <ChatInput
                            inputRef={inputRef}
                            input={input}
                            setInput={setInput}
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                            isExecutingTool={isExecutingTool}
                            stop={stop}
                            quota={quota}
                            size="sm"
                            formStyle={{ padding: '8px 16px' }}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            isGlobalDragging={isDragging}
                        />
                    </Paper>
                )}
            </Transition>
        </>
    );
}